// src/components/itinerary/ActivityList.tsx
// src/components/itinerary/ActivityList.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Search as SearchIcon,
  Close as ClearIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Activity } from '../../contexts/ItineraryContext';
import ActivityCard from './ActivityCard';

interface ActivityListProps {
  activities: Activity[];
  isGenerating: boolean;
  destination: string;
  onGenerateActivities: () => void;
  onEditActivity: (activity: Activity) => void;
  onAddCustomActivity: () => void;
}

const ActivityList: React.FC<ActivityListProps> = ({
  activities,
  isGenerating,
  destination,
  onGenerateActivities,
  onEditActivity,
  onAddCustomActivity
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  
  // Filter activities based on search term and type filter
  const filteredActivities = activities.filter(activity => {
    const matchesSearch = searchTerm === '' || 
      activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.location.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesFilter = selectedFilter === null || activity.type === selectedFilter;
    
    return matchesSearch && matchesFilter;
  });
  
  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  // Handle filter selection
  const handleFilterSelect = (type: string | null) => {
    setSelectedFilter(type);
    setFilterDialogOpen(false);
  };
  
  // Reset filters
  const resetFilters = () => {
    setSelectedFilter(null);
    setFilterDialogOpen(false);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      overflow: 'hidden'
    }}>
      <Box sx={{ mb: 2, flexShrink: 0 }}> {/* Control section - fixed size */}
        <Typography variant="h6" gutterBottom>
          Activity Suggestions
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search activities..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFilterDialogOpen(true)}
            color={selectedFilter ? "primary" : "inherit"}
            size="small"
          >
            Filter
          </Button>
        </Box>
        
        {selectedFilter && (
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Chip 
              label={`Type: ${selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}`}
              onDelete={resetFilters}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={onAddCustomActivity}
            size="small"
          >
            Add Custom
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<MagicIcon />}
            onClick={onGenerateActivities}
            disabled={isGenerating}
            size="small"
          >
            {isGenerating ? 'Generating...' : 'Generate More'}
          </Button>
        </Box>
      </Box>
      
      {/* Activity list section - takes remaining height */}
      <Box sx={{ 
        flex: 1,
        position: 'relative',
        overflow: 'hidden' 
      }}>
        {isGenerating ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)'
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Generating activities for {destination}...
            </Typography>
          </Box>
        ) : filteredActivities.length === 0 ? (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 3
          }}>
            {activities.length === 0 ? (
              <>
                <Typography variant="body1" paragraph textAlign="center">
                  No activity suggestions yet.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<MagicIcon />}
                  onClick={onGenerateActivities}
                >
                  Generate Activities
                </Button>
              </>
            ) : (
              <Typography variant="body1" textAlign="center">
                No activities match your search or filters.
              </Typography>
            )}
          </Box>
        ) : (
          <Droppable droppableId="activity-list">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ 
                  height: '100%',
                  overflow: 'auto',
                  pr: 1 // Room for scrollbar
                }}
              >
                {filteredActivities.map((activity, index) => (
                  <Draggable
                    key={activity.id}
                    draggableId={activity.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <ActivityCard
                          activity={activity}
                          isDragging={snapshot.isDragging}
                          dragHandleProps={provided.dragHandleProps}
                          onEdit={onEditActivity}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        )}
      </Box>
      
      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        PaperProps={{
          sx: { position: 'fixed' } // Ensures dialog doesn't cause page shift
        }}
      >
        <DialogTitle>Filter Activities</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Activity Type
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {['activity', 'food', 'accommodation', 'transportation'].map((type) => (
                <Chip
                  key={type}
                  label={type.charAt(0).toUpperCase() + type.slice(1)}
                  onClick={() => handleFilterSelect(type)}
                  color={selectedFilter === type ? 'primary' : 'default'}
                  variant={selectedFilter === type ? 'filled' : 'outlined'}
                  clickable
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilters} color="inherit">
            Reset
          </Button>
          <Button onClick={() => setFilterDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityList;